import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, RefreshCw, X, ChevronRight, AlertTriangle, Timer, Filter,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import {
  pvDashboardSummary, pvDashboardLocation, pvStatuses,
} from "@/lib/api";

const SERVICE_OPTIONS = [
  "All", "General Service", "Body Repair", "Mechanical Repair",
  "Warranty Service", "Accident Repair",
];

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const PvDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, locations } = useApp();

  const allowedLocs = useMemo(
    () => locations.filter(
      (l) => currentUser?.role === "admin" ||
             currentUser?.role === "crm" ||
             (currentUser?.location_codes || []).includes(l.code),
    ),
    [locations, currentUser],
  );

  const defaultLocation = useMemo(() => {
    if (currentUser?.role === "admin" || currentUser?.role === "crm") return "";
    return (currentUser?.location_codes || [])[0] || "";
  }, [currentUser]);

  // Pending filters (edited by the user, applied on button click)
  const [pLocation, setPLocation] = useState(defaultLocation);
  const [pMonth, setPMonth] = useState(currentMonth());
  const [pStatus, setPStatus] = useState("");
  const [pService, setPService] = useState("All");
  // Applied filters (used by requests)
  const [aLocation, setALocation] = useState(defaultLocation);
  const [aMonth, setAMonth] = useState(currentMonth());
  const [aStatus, setAStatus] = useState("");
  const [aService, setAService] = useState("All");

  const [data, setData] = useState({ kpis: {}, rows: [], month: aMonth });
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [detailFor, setDetailFor] = useState(null); // location code selected

  const loadStatuses = useCallback(async () => {
    try { const d = await pvStatuses(false); setStatusOptions(d.rows || []); }
    catch {/* silent */}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await pvDashboardSummary({
        month: aMonth || undefined,
        location_code: aLocation || undefined,
        service_type: aService && aService !== "All" ? aService : undefined,
        pv_status_id: aStatus || undefined,
      });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  }, [aMonth, aLocation, aService, aStatus]);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);
  useEffect(() => { load(); }, [load]);

  const apply = () => { setALocation(pLocation); setAMonth(pMonth); setAStatus(pStatus); setAService(pService); };
  const clear = () => {
    setPLocation(defaultLocation); setPMonth(currentMonth()); setPStatus(""); setPService("All");
    setALocation(defaultLocation); setAMonth(currentMonth()); setAStatus(""); setAService("All");
  };

  const gotoWb = (code, extra = {}) => {
    const params = new URLSearchParams({ location_code: code, ...extra });
    navigate(`/pv/workbench?${params.toString()}`);
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pv-dashboard-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <LayoutGrid className="w-6 h-6 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold text-yamaha-800 leading-none tracking-tight">
            PV Dashboard
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            Location-first pendency overview for {data.month || aMonth}. Click any row to open the workbench filtered by that location.
          </p>
        </div>
        <button onClick={load} data-testid="pv-dash-refresh"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Total Active Pending" value={data.kpis?.total_active_pending ?? 0}
          tone="yamaha" icon={<LayoutGrid className="w-5 h-5 text-yamaha-700" />} testId="pv-kpi-active" />
        <Kpi label="Total EDD Missed" value={data.kpis?.total_edd_missed ?? 0}
          tone="rose" icon={<AlertTriangle className="w-5 h-5 text-rose-700" />} testId="pv-kpi-missed" />
        <Kpi label="Total >7 Days Pending" value={data.kpis?.total_gt7_pending ?? 0}
          tone="orange" icon={<Timer className="w-5 h-5 text-orange-700" />} testId="pv-kpi-gt7" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#747684]" />
        <select data-testid="pv-dash-loc" value={pLocation} onChange={(e) => setPLocation(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          <option value="">All Locations</option>
          {allowedLocs.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
        </select>
        <input type="month" data-testid="pv-dash-month" value={pMonth} onChange={(e) => setPMonth(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
        <select data-testid="pv-dash-status" value={pStatus} onChange={(e) => setPStatus(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          <option value="">All PV Statuses</option>
          {statusOptions.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
        </select>
        <select data-testid="pv-dash-service" value={pService} onChange={(e) => setPService(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={apply} data-testid="pv-dash-apply"
          className="px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[11.5px] font-semibold">Apply</button>
        <button onClick={clear} data-testid="pv-dash-clear"
          className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[11.5px] font-semibold text-[#444653] hover:bg-[#faf8ff]">Clear</button>
      </div>

      {/* Location grid */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="pv-dash-loc-grid">
            <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-center">Active Pending</th>
                <th className="px-3 py-2 text-center">EDD Missed</th>
                <th className="px-3 py-2 text-center">Ready for Delivery</th>
                <th className="px-3 py-2 text-center">&gt;7 Days</th>
                <th className="px-3 py-2 text-center">No EDD</th>
                <th className="px-3 py-2 text-center">Monthly Total</th>
                <th className="px-3 py-2 text-center">Avg Turnaround</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eeecf6]">
              {data.rows.length === 0 && (
                <tr><td colSpan={9} className="text-center text-[12.5px] text-[#747684] py-10">
                  {loading ? "Loading…" : "No data for the selected filters."}
                </td></tr>
              )}
              {data.rows.map((r) => (
                <tr key={r.location_code} data-testid={`pv-dash-row-${r.location_code}`}
                  className="hover:bg-[#faf8ff] cursor-pointer" onClick={() => setDetailFor(r.location_code)}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-[#1a1b22]">{r.location_name}</div>
                    <div className="text-[10.5px] text-[#747684] font-mono">{r.location_code}</div>
                  </td>
                  <td className="px-3 py-2 text-center font-semibold text-yamaha-700">{r.active_pending}</td>
                  <td className="px-3 py-2 text-center">
                    {r.edd_missed > 0
                      ? <span className="text-rose-700 font-semibold">{r.edd_missed}</span>
                      : <span className="text-[#747684]">0</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-teal-700 font-semibold">{r.ready_for_delivery}</td>
                  <td className="px-3 py-2 text-center">
                    {r.gt7_pending > 0
                      ? <span className="text-orange-700 font-semibold">{r.gt7_pending}</span>
                      : <span className="text-[#747684]">0</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-[#444653]">{r.no_edd}</td>
                  <td className="px-3 py-2 text-center text-[#444653]">{r.monthly_total_pending}</td>
                  <td className="px-3 py-2 text-center text-[#444653]">
                    {r.avg_turnaround_days == null ? "—" : `${r.avg_turnaround_days} d`}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`pv-dash-open-${r.location_code}`}
                      onClick={(e) => { e.stopPropagation(); gotoWb(r.location_code); }}
                      className="text-[11px] text-yamaha-700 hover:underline">Open Workbench →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailFor && (
        <LocationDetailDrawer
          code={detailFor}
          month={aMonth}
          onClose={() => setDetailFor(null)}
          onOpenWb={(extra) => gotoWb(detailFor, extra)}
        />
      )}
    </div>
  );
};

const Kpi = ({ label, value, tone = "yamaha", icon, testId }) => {
  const tones = {
    yamaha: "bg-yamaha-50 border-yamaha-100",
    rose:   "bg-rose-50 border-rose-200",
    orange: "bg-orange-50 border-orange-200",
  };
  return (
    <div className={`border rounded-xl p-4 ${tones[tone] || tones.yamaha}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#747684] font-semibold">{label}</div>
          <div className="text-[26px] font-semibold text-[#1a1b22] mt-1 leading-none">{value}</div>
        </div>
        <div className="w-9 h-9 bg-white/60 rounded-lg flex items-center justify-center">{icon}</div>
      </div>
    </div>
  );
};

// -------- Location detail drawer -------------------------------------
const LocationDetailDrawer = ({ code, month, onClose, onOpenWb }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await pvDashboardLocation(code, { month });
        if (!alive) return;
        setD(r);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load location detail");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [code, month]);

  const ageingCells = [
    { key: "0-1",  label: "0 - 1 Day",     tone: "bg-emerald-50 text-emerald-700 border-emerald-200", filter: { ageing_bucket: "0-3" } },
    { key: "2-3",  label: "2 - 3 Days",    tone: "bg-amber-50 text-amber-700 border-amber-200",       filter: { ageing_bucket: "0-3" } },
    { key: "4-7",  label: "4 - 7 Days",    tone: "bg-orange-50 text-orange-700 border-orange-200",    filter: { ageing_bucket: "4-7" } },
    { key: "gt7",  label: "> 7 Days",      tone: "bg-rose-50 text-rose-700 border-rose-200",          filter: { ageing_bucket: "8-15" } },
  ];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="pv-dash-detail-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-[560px] bg-white border-l border-[#e2e1eb] h-full overflow-y-auto shadow-xl">
        <div className="px-5 py-4 border-b border-[#e2e1eb] flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-yamaha-50 flex items-center justify-center">
            <LayoutGrid className="w-5 h-5 text-yamaha-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-[#1a1b22]">Location Detail · {code}</h3>
            <div className="text-[11.5px] text-[#747684] mt-0.5">Month {d?.month || month}</div>
          </div>
          <button data-testid="pv-dash-detail-close" onClick={onClose} className="text-[#747684] hover:text-[#1a1b22]"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="text-[13px] text-[#747684]">Loading…</div>
          ) : d ? (
            <>
              {/* Ageing */}
              <section>
                <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-[#747684] mb-2">Ageing (Active Pending)</h4>
                <div className="grid grid-cols-4 gap-2">
                  {ageingCells.map((c) => (
                    <button key={c.key} data-testid={`pv-dash-ageing-${c.key}`}
                      onClick={() => onOpenWb({ ageing_bucket: c.filter.ageing_bucket })}
                      className={`border rounded-lg py-3 px-2 text-center hover:opacity-90 ${c.tone}`}>
                      <div className="text-[20px] font-semibold leading-none">{d.ageing_breakdown[c.key] || 0}</div>
                      <div className="text-[10.5px] mt-1">{c.label}</div>
                    </button>
                  ))}
                </div>
              </section>

              {/* Status */}
              <section>
                <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-[#747684] mb-2">Status Breakdown</h4>
                <div className="border border-[#e2e1eb] rounded-lg divide-y divide-[#eeecf6]" data-testid="pv-dash-status-list">
                  {d.status_breakdown.map((s) => (
                    <div key={s.status_name} className="flex items-center justify-between px-3 py-1.5 text-[12.5px]">
                      <span className="text-[#1a1b22]">{s.status_name}</span>
                      <span className="font-mono font-semibold text-[#444653]">{s.count}</span>
                    </div>
                  ))}
                  {d.status_breakdown.length === 0 && (
                    <div className="px-3 py-3 text-[12px] text-[#747684]">No PV statuses configured.</div>
                  )}
                </div>
              </section>

              {/* Service type */}
              <section>
                <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-[#747684] mb-2">Service Type Performance</h4>
                <div className="border border-[#e2e1eb] rounded-lg overflow-hidden" data-testid="pv-dash-service-list">
                  <table className="w-full text-[12px]">
                    <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left">Service Type</th>
                        <th className="px-3 py-1.5 text-center">Monthly Pending</th>
                        <th className="px-3 py-1.5 text-center">Avg Turnaround</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#eeecf6]">
                      {d.service_type_breakdown.map((s) => (
                        <tr key={s.service_type}>
                          <td className="px-3 py-1.5 text-[#1a1b22]">{s.service_type}</td>
                          <td className="px-3 py-1.5 text-center font-semibold text-[#444653]">{s.monthly_pending}</td>
                          <td className="px-3 py-1.5 text-center text-[#444653]">
                            {s.avg_turnaround_days == null ? "—" : `${s.avg_turnaround_days} d`}
                          </td>
                        </tr>
                      ))}
                      {d.service_type_breakdown.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-[12px] text-[#747684] py-3">No service-type activity yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <button data-testid="pv-dash-open-full-wb"
                onClick={() => onOpenWb({})}
                className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12.5px] font-semibold">
                Open Workbench <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <div className="text-[13px] text-[#747684]">Unable to load detail.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PvDashboard;
